import { useEffect, useReducer, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { useLang } from "../../contexts/LangContext";
import { tx } from "../../i18n";
import { useAuth } from "../../contexts/AuthContext";
import { profileService, cashAccountsService } from "../../services/profileService";
import { receivablesService, paymentsService } from "../../services/transactionsService";
import { investmentsService, debtsService, goalsService } from "../../services/wealthService";
import { toast } from "sonner";
import { reportValidationError } from "../../lib/validationTelemetry";
import { Step1ProfileSchema } from "../../lib/onboardingSchemas";
import { ArrowRight, ArrowLeft, SkipForward } from "lucide-react";
import { api } from "../../lib/api";
import {
  StepProfile, StepMoney, StepReceivables, StepPayments, StepWithdrawal, StepWealth,
} from "./OnboardingSteps";
import { Stepper } from "../../components/ui/stepper";

const TOTAL_STEPS = 6;

// Best-effort onboarding telemetry. Fire-and-forget — failures here MUST
// NOT block the wizard. Backend stores the event in `onboarding_events`
// for the admin funnel card.
function trackOnboardingEvent(step, kind, meta) {
  api.post("/onboarding/event", { step, kind, meta: meta || {} }).catch(() => {});
}

const initialState = {
  step: 1,
  submitting: false,
  profile: { full_name: "", professional_activity: "Freelance Designer", currency: "USD", avg_monthly_income: "" },
  // Step 2 — Bank is mandatory (always visible). Cash/Wallet start hidden so
  // the user only sees what they actually use; they can opt-in via the
  // "+ Add Cash / + Add Wallet" buttons in the step itself.
  accounts: [
    { type: "cash", name: "Cash", balance: "", visible: false },
    { type: "bank", name: "Bank account", balance: "", visible: true },
    { type: "wallet", name: "Digital wallet", balance: "", visible: false },
  ],
  receivables: [],
  recv: { client_name: "", amount: "", expected_date: "", probability: "high" },
  recvErrors: {},
  payments: [],
  pay: { concept: "", amount: "", due_date: "", type: "personal", priority: "medium" },
  payErrors: {},
  withdrawal: { amount: "", reason: "" },
  withdrawalErrors: {},
  investments: [],
  inv: { name: "", kind: "stocks", amount_invested: "" },
  invErrors: {},
  debts: [],
  debtForm: { name: "", kind: "credit_card", balance: "" },
  debtErrors: {},
  goals: [],
  goalForm: { name: "", kind: "travel", target_amount: "" },
  goalErrors: {},
};

/** All onboarding state mutations funnel through this reducer.
 * SET   sets a single top-level field (form blocks or scalars)
 * STEP  jumps to a target step (clamped to [1, TOTAL_STEPS])
 * PUSH  appends to one of the list fields (receivables/payments/...)
 * DROP  removes by id from one of the list fields
 * RESET resets the matching form block to its initial empty shape
 */
function reducer(state, action) {
  switch (action.type) {
    case "SET":
      return { ...state, [action.key]: action.value };
    case "STEP":
      return { ...state, step: Math.max(1, Math.min(TOTAL_STEPS, action.value)) };
    case "PUSH":
      return { ...state, [action.key]: [...state[action.key], action.item] };
    case "DROP":
      return { ...state, [action.key]: state[action.key].filter((x) => x.id !== action.id) };
    case "RESET":
      return { ...state, [action.key]: initialState[action.key] };
    default:
      return state;
  }
}

export default function Onboarding() {
  const { t, lang, setLang } = useLang();
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(reducer, initialState);

  // Onboarded users that land here by accident (deep-link, browser back, etc.)
  // are bounced to the dashboard to avoid overwriting their profile/accounts.
  useEffect(() => {
    if (user?.onboarded) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  // Generic helper for child components: returns a stable setter that swaps
  // one of the form blocks via a functional updater, matching useState's API.
  const setField = (key) => (updater) =>
    dispatch({ type: "SET", key, value: typeof updater === "function" ? updater(state[key]) : updater });
  const setStep = (value) => dispatch({ type: "STEP", value });

  // preload profile if exists (run once on mount)
  useEffect(() => {
    profileService.get().then((data) => {
      if (data) dispatch({
        type: "SET",
        key: "profile",
        value: {
          full_name: data.full_name || "",
          professional_activity: data.professional_activity || "Freelance Designer",
          currency: data.currency || "USD",
          avg_monthly_income: data.avg_monthly_income || "",
        },
      });
    }).catch((e) => console.warn("profile preload failed", e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { step, submitting, profile, accounts, receivables, recv, recvErrors, payments, pay, payErrors,
          withdrawal, withdrawalErrors,
          investments, inv, invErrors, debts, debtForm, debtErrors,
          goals, goalForm, goalErrors } = state;
  const next = () => setStep(step + 1);
  const prev = () => setStep(step - 1);
  const setSubmitting = (value) => dispatch({ type: "SET", key: "submitting", value });

  // iter-169zzg — `forceShowErrors` toggles to make StepProfile surface
  // its inline Zod errors when the parent's Next-click safeParse fails.
  // Pure UI state — does not need to live in the reducer (no re-entry).
  const [step1ForceShow, setStep1ForceShow] = useState(false);

  // Fire an "enter" event whenever the active step changes. This is what
  // the admin funnel uses to count distinct users per step.
  useEffect(() => {
    trackOnboardingEvent(step, "enter");
  }, [step]);

  const saveProfile = async () => {
    await profileService.update({
      ...profile,
      avg_monthly_income: Number(profile.avg_monthly_income || 0),
    });
  };

  const saveAccounts = async () => {
    // Wipe existing demo accounts and create new ones (simple MVP behavior).
    // Only persist accounts the user opted to keep visible AND with a
    // non-zero balance — hidden rows mean "I don't use this account type".
    const existing = await cashAccountsService.list();
    for (const a of existing) {
      await cashAccountsService.remove(a.id);
    }
    for (const a of accounts) {
      if (a.visible === false) continue;
      const bal = Number(a.balance || 0);
      if (bal > 0) {
        await cashAccountsService.create({ type: a.type, name: a.name, balance: bal });
      }
    }
  };

  const handleStepNext = async () => {
    try {
      if (step === 1) {
        // iter-169zzg · uses the shared Step1ProfileSchema (also imported
        // by StepProfile for inline error display). ``installZodErrorMap``
        // (LangContext) ensures the message comes out in the active
        // language without per-form copy.
        const result = Step1ProfileSchema.safeParse(profile);
        if (!result.success) {
          const fe = {};
          for (const issue of result.error.issues || []) {
            const k = issue.path[issue.path.length - 1];
            if (k && !fe[k]) fe[k] = issue.message;
          }
          // Telemetry — surfaces as ``onb-step1`` in the admin dashboard
          // so we see how many users bounce on the first screen.
          reportValidationError("onb-step1", fe);
          // iter-169zzg · flip + clear in microtask so the StepProfile
          // useEffect re-runs even if the flag was already true on
          // back-to-back clicks of "Next".
          setStep1ForceShow(false);
          setTimeout(() => setStep1ForceShow(true), 0);
          toast.error(tx(lang, "Enter your name to continue.", "Ingresa tu nombre para continuar.", "Ingresa tu nombre para continuar."));
          const el = document.getElementById("onb-full-name-input");
          if (el) {
            el.focus();
            el.scrollIntoView({ behavior: "smooth", block: "center" });
          }
          return;
        }
        await saveProfile();
        trackOnboardingEvent(1, "complete");
        next();
      } else if (step === 2) {
        await saveAccounts();
        trackOnboardingEvent(2, "complete");
        next();
      } else if (step === 3 || step === 4) {
        trackOnboardingEvent(step, "complete");
        next();
      } else if (step === 5) {
        // Save the desired withdrawal but DO NOT navigate yet — show optional wealth step.
        await profileService.setWithdrawal({
          amount: Number(withdrawal.amount || 0),
          reason: withdrawal.reason,
        });
        trackOnboardingEvent(5, "complete");
        next();
      } else if (step === 6) {
        // Wealth items have been saved as the user added them — just finalize.
        trackOnboardingEvent(6, "complete");
        trackOnboardingEvent(6, "finish");
        await refresh();
        toast.success("All set! Calculating your dashboard...");
        navigate("/dashboard");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSubmitting(false);
    }
  };

  // ----- List add/remove helpers (each isolates its own try/catch + reset) -----
  // iter-169zze · `validator` now returns a field-error map (e.g.
  // ``{ amount: "Indica un monto" }``) instead of a single toast
  // string. Empty map ⇒ valid. We store the map in
  // ``state[errorsKey]`` so each step can highlight offending inputs
  // inline (red border + form-err-msg) and clear them as the user
  // types. Backwards-compat: a return value of ``null`` / ``{}`` /
  // ``undefined`` all mean "no errors".
  const addItem = (formKey, listKey, errorsKey, validator, builder, service) => async () => {
    const form = state[formKey];
    const errs = validator(form) || {};
    if (Object.keys(errs).length) {
      dispatch({ type: "SET", key: errorsKey, value: errs });
      return;
    }
    dispatch({ type: "SET", key: errorsKey, value: {} });
    try {
      const data = await service.create(builder(form));
      dispatch({ type: "PUSH", key: listKey, item: data });
      dispatch({ type: "RESET", key: formKey });
    } catch {
      toast.error(tx(lang, "Could not add.", "No se pudo agregar.", "No se pudo agregar."));
    }
  };
  const removeItem = (listKey, service) => async (id) => {
    await service.remove(id);
    dispatch({ type: "DROP", key: listKey, id });
  };

  const isEs = lang === "es";

  const addReceivable = addItem(
    "recv", "receivables", "recvErrors",
    (f) => {
      const e = {};
      if (!f.client_name?.trim()) e.client_name = isEs ? "Nombre del cliente obligatorio." : "Client name required.";
      if (!f.amount || Number(f.amount) <= 0) e.amount = isEs ? "Indica un monto mayor a 0." : "Enter an amount > 0.";
      if (!f.expected_date) e.expected_date = isEs ? "Elige la fecha esperada." : "Pick the expected date.";
      return e;
    },
    (f) => ({
      client_name: f.client_name,
      amount: Number(f.amount),
      expected_date: new Date(f.expected_date).toISOString(),
      probability: f.probability,
    }),
    receivablesService,
  );
  const addPayment = addItem(
    "pay", "payments", "payErrors",
    (f) => {
      const e = {};
      if (!f.concept?.trim()) e.concept = isEs ? "El concepto es obligatorio." : "Concept required.";
      if (!f.amount || Number(f.amount) <= 0) e.amount = isEs ? "Indica un monto mayor a 0." : "Enter an amount > 0.";
      if (!f.due_date) e.due_date = isEs ? "Elige la fecha de vencimiento." : "Pick the due date.";
      return e;
    },
    (f) => ({
      concept: f.concept,
      amount: Number(f.amount),
      due_date: new Date(f.due_date).toISOString(),
      type: f.type,
      priority: f.priority,
    }),
    paymentsService,
  );
  const addInvestment = addItem(
    "inv", "investments", "invErrors",
    (f) => {
      const e = {};
      if (!f.name?.trim()) e.name = isEs ? "Nombre obligatorio." : "Name required.";
      if (!f.amount_invested || Number(f.amount_invested) <= 0) e.amount_invested = isEs ? "Indica el monto invertido." : "Enter amount invested.";
      return e;
    },
    (f) => ({
      name: f.name, kind: f.kind, ticker: null,
      amount_invested: Number(f.amount_invested), current_value: Number(f.amount_invested),
      currency: profile.currency || "USD",
    }),
    investmentsService,
  );
  const addDebt = addItem(
    "debtForm", "debts", "debtErrors",
    (f) => {
      const e = {};
      if (!f.name?.trim()) e.name = isEs ? "Nombre obligatorio." : "Name required.";
      if (!f.balance || Number(f.balance) <= 0) e.balance = isEs ? "Indica el saldo actual." : "Enter current balance.";
      return e;
    },
    (f) => ({ name: f.name, kind: f.kind, principal: Number(f.balance), balance: Number(f.balance) }),
    debtsService,
  );
  const addGoal = addItem(
    "goalForm", "goals", "goalErrors",
    (f) => {
      const e = {};
      if (!f.name?.trim()) e.name = isEs ? "Nombre obligatorio." : "Name required.";
      if (!f.target_amount || Number(f.target_amount) <= 0) e.target_amount = isEs ? "Indica el monto meta." : "Enter target amount.";
      return e;
    },
    (f) => ({ name: f.name, kind: f.kind, target_amount: Number(f.target_amount), current_amount: 0 }),
    goalsService,
  );

  const removeReceivable = removeItem("receivables", receivablesService);
  const removePayment = removeItem("payments", paymentsService);
  const removeInvestment = removeItem("investments", investmentsService);
  const removeDebt = removeItem("debts", debtsService);
  const removeGoal = removeItem("goals", goalsService);

  const skipWealth = async () => {
    setSubmitting(true);
    try {
      trackOnboardingEvent(6, "skip");
      trackOnboardingEvent(6, "finish");
      await refresh();
      navigate("/dashboard");
    } finally {
      setSubmitting(false);
    }
  };

  // Steps 3 (receivables) and 4 (payments) are also optional — the user might
  // have no upcoming items. We expose a "Skip" affordance directly on the
  // footer so they don't have to fake-add a row to advance.
  const isStepSkippable = step === 3 || step === 4 || step === 6;
  const skipStep = step === 6 ? skipWealth : () => {
    // For optional in-funnel steps we record the skip and let the
    // normal next() bump the wizard forward. The skip event is what
    // ops use to differentiate "completed (engaged)" from "skipped
    // (low-quality lead)" in the admin funnel.
    trackOnboardingEvent(step, "skip");
    next();
  };

  // Per-step gating: disable "Continue" when the user hasn't satisfied the
  // step's bare minimum. We avoid the previous pattern of letting them click
  // Continue → toast error → re-edit; instead the button is muted until the
  // form is acceptable.
  const canProceed = (() => {
    if (submitting) return false;
    if (step === 1) return Boolean(profile.full_name?.trim());
    // Step 2: any visible row with a balance entered (>= 0). We don't force
    // a non-zero balance because some users start with empty accounts.
    if (step === 2) return accounts.some((a) => a.visible !== false && a.balance !== "");
    // Steps 3/4/6 are optional → always allow proceeding.
    if (step === 3 || step === 4 || step === 6) return true;
    // Step 5: desired-withdrawal amount must be entered.
    if (step === 5) return Number(withdrawal.amount || 0) > 0;
    return true;
  })();

  return (
    <div className="min-h-screen bg-cream-100 py-8 px-4 relative overflow-hidden">
      {/* Editorial ambient halos — same DNA as landing */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 -right-32 h-[420px] w-[420px] rounded-full bg-emerald-200/30 blur-3xl landing-ambient" />
        <div className="absolute top-1/2 -left-32 h-[360px] w-[360px] rounded-full bg-amber-200/25 blur-3xl landing-ambient" style={{ animationDelay: "3s" }} />
      </div>

      <div className="relative max-w-2xl mx-auto">
        {/* Brand bar + language switch. The onboarding is the user's
            first signed-in surface, so the lang selector must live here
            (not behind a settings menu) — otherwise an es-speaker who
            signed up via an en-language landing has no way to switch
            before completing the flow. */}
        <div className="flex items-center justify-between mb-7 fade-up">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-xl bg-slate-900 text-white flex items-center justify-center font-display font-bold text-lg">L</div>
            <div className="flex flex-col leading-none">
              <span className="font-serif-display text-xl font-medium text-slate-900 tracking-tight">Lumfi</span>
              <span className="text-[9px] uppercase tracking-[0.18em] font-semibold text-emerald-700 mt-0.5">Cash Pilot</span>
            </div>
          </div>
          <div className="flex items-center gap-1 rounded-full bg-white/80 backdrop-blur border border-stone-200 p-1 text-[11px] font-semibold uppercase tracking-wider" data-testid="onboarding-lang-switch">
            {[
              { v: "es", label: "ES" },
              { v: "en", label: "EN" },
              { v: "pt", label: "PT" },
            ].map((opt) => (
              <button /* design-system: ALLOW legacy-todo */
                key={opt.v}
                type="button"
                onClick={() => setLang(opt.v)}
                data-testid={`onboarding-lang-${opt.v}`}
                className={`px-2.5 py-1 rounded-full transition-colors ${lang === opt.v ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <Card className="p-5 sm:p-7 md:p-10 rounded-3xl border border-cream-300 bg-white shadow-[0_18px_48px_-30px_rgba(15,23,42,0.18)]" data-testid={`onboarding-step-${step}`}>
          {/* iter-169zq · Stepper unificado (mobile bar / desktop dots).
              Mantengo el label de paso (emerald) abajo para reforzar
              contexto sin robar altura en móvil. */}
          <Stepper
            current={step}
            total={6}
            labels={[
              tx(lang, "Profile", "Perfil", "Perfil"),
              tx(lang, "Money", "Cuentas", "Cuentas"),
              tx(lang, "Income", "Cobros", "Cobros"),
              tx(lang, "Bills", "Pagos", "Pagos"),
              tx(lang, "Withdraw", "Retiro", "Retiro"),
              tx(lang, "Wealth", "Wealth", "Wealth"),
            ]}
            onStepClick={(s) => dispatch({ type: "STEP", value: s })}
          />
          <div className="mt-3 md:mt-1 mb-4 text-center md:text-left">
            <span className="text-xs font-bold uppercase tracking-[0.16em] text-emerald-700">
              {step === 1 && t("profile_setup")}
              {step === 2 && t("money_title")}
              {step === 3 && t("receivables_title")}
              {step === 4 && t("payments_title")}
              {step === 5 && t("withdrawal_title")}
              {step === 6 && (tx(lang, "Wealth (optional)", "Patrimonio (opcional)", "Patrimonio (opcional)"))}
            </span>
          </div>

          {/* Step body — keyed so each step transition re-plays the fade-up.
              That gives the user a continuous editorial "page-turn" feel
              instead of a static form. */}
          <div key={step} className="fade-up">
            {step === 1 && <StepProfile t={t} lang={lang} profile={profile} setProfile={setField("profile")} forceShowErrors={step1ForceShow} />}

            {step === 2 && <StepMoney t={t} lang={lang} currency={profile.currency || "USD"} accounts={accounts} setAccounts={setField("accounts")} />}

            {step === 3 && (
              <StepReceivables
                t={t}
                lang={lang}
                currency={profile.currency || "USD"}
                recv={recv}
                setRecv={setField("recv")}
                receivables={receivables}
                addReceivable={addReceivable}
                removeReceivable={removeReceivable}
                errors={recvErrors}
                setErrors={setField("recvErrors")}
              />
            )}

            {step === 4 && (
              <StepPayments
                t={t}
                lang={lang}
                currency={profile.currency || "USD"}
                pay={pay}
                setPay={setField("pay")}
                payments={payments}
                addPayment={addPayment}
                removePayment={removePayment}
                errors={payErrors}
                setErrors={setField("payErrors")}
              />
            )}

            {step === 5 && (
              <StepWithdrawal t={t} lang={lang} currency={profile.currency || "USD"} withdrawal={withdrawal} setWithdrawal={setField("withdrawal")} errors={withdrawalErrors} setErrors={setField("withdrawalErrors")} />
            )}

            {step === 6 && (
              <StepWealth
                lang={lang}
                inv={inv}
                setInv={setField("inv")}
                investments={investments}
                addInvestment={addInvestment}
                removeInvestment={removeInvestment}
                invErrors={invErrors}
                setInvErrors={setField("invErrors")}
                debtForm={debtForm}
                setDebtForm={setField("debtForm")}
                debts={debts}
                addDebt={addDebt}
                removeDebt={removeDebt}
                debtErrors={debtErrors}
                setDebtErrors={setField("debtErrors")}
                goalForm={goalForm}
                setGoalForm={setField("goalForm")}
                goals={goals}
                addGoal={addGoal}
                removeGoal={removeGoal}
                goalErrors={goalErrors}
                setGoalErrors={setField("goalErrors")}
              />
            )}
          </div>

          {/* iter-169zq · Action bar:
              • Mobile: sticky bottom (thumb-zone), full-width buttons.
              • Desktop: inline en el footer del card, ghost back left,
                next right. */}
          <div className="mt-8 hidden md:flex items-center justify-between gap-3 pt-6 border-t border-cream-300">
            <Button
              type="button"
              variant="ghost"
              onClick={prev}
              disabled={step === 1}
              data-testid="onb-back-btn"
              className="gap-1 text-slate-600 hover:text-slate-900 h-12"
            >
              <ArrowLeft className="h-4 w-4" />{t("back")}
            </Button>
            <div className="flex items-center gap-2">
              {isStepSkippable && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={skipStep}
                  disabled={submitting}
                  data-testid="onb-skip-btn"
                  className="text-slate-500 gap-1 h-12"
                >
                  <SkipForward className="h-4 w-4" />
                  {tx(lang, "Skip", "Saltar", "Saltar")}
                </Button>
              )}
              <Button
                type="button"
                onClick={handleStepNext}
                disabled={!canProceed}
                data-testid="onb-next-btn"
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-7 h-12 gap-2 shadow-[0_8px_24px_-8px_rgba(22,163,74,0.45)] hover:translate-y-[-1px] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              >
                {step === 6 ? (tx(lang, "Go to dashboard", "Ir al dashboard", "Ir al dashboard")) : step === 5 ? (tx(lang, "Continue", "Continuar", "Continuar")) : t("continue")} <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </Card>

        {/* Mobile sticky action bar — sits below the card. The page has
            bottom padding so the last form field never hides behind it. */}
        <div className="md:hidden h-24" aria-hidden="true" />
        <div className="md:hidden fixed inset-x-0 bottom-0 z-30 border-t border-stone-200 bg-white/95 backdrop-blur px-4 py-3 flex items-center gap-2 lumfi-thumb-zone" data-testid="onb-action-bar-mobile">
          <Button
            type="button"
            variant="outline"
            onClick={prev}
            disabled={step === 1}
            data-testid="onb-back-btn-mobile"
            className="h-12 px-3 border-slate-300"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          {isStepSkippable && (
            <Button
              type="button"
              variant="ghost"
              onClick={skipStep}
              disabled={submitting}
              data-testid="onb-skip-btn-mobile"
              className="h-12 px-3 text-slate-500"
            >
              {tx(lang, "Skip", "Saltar", "Saltar")}
            </Button>
          )}
          <Button
            type="button"
            onClick={handleStepNext}
            disabled={!canProceed}
            data-testid="onb-next-btn-mobile"
            className="flex-1 h-12 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold gap-2 disabled:opacity-50"
          >
            {step === 6 ? (tx(lang, "Go to dashboard", "Ir al dashboard", "Ir al dashboard")) : step === 5 ? (tx(lang, "Continue", "Continuar", "Continuar")) : t("continue")}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
